import {
  DataGridPremiumProps,
  GridApiPremium,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { RefObject, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CacheLine } from "src/store/api/generatedApi";
import {
  buildCacheColumns,
  cacheColumnsAggregationFunctions,
  cacheColumnsAggregationModel,
} from "./cacheColumns";
import { useLocalDatetime } from "hooks/useLocalDateTime";

const rowGroupingModel: DataGridPremiumProps["rowGroupingModel"] = [
  "category",
  "type_name",
];
const initialState: DataGridPremiumProps["initialState"] = {
  columns: {
    columnVisibilityModel: {
      category: false,
      type_name: false,
      key: false,
      original_id: false,
      id: false,
    },
  },
};
const getAggregationPosition: DataGridPremiumProps["getAggregationPosition"] =
  () => "inline" as const;
const isGroupExpandedByDefault: DataGridPremiumProps["isGroupExpandedByDefault"] =
  (node: { groupingField?: string | null }) =>
    node.groupingField === "category";
const slotProps: DataGridPremiumProps["slotProps"] = {
  loadingOverlay: {
    variant: "skeleton",
    noRowsVariant: "skeleton",
  },
};
const toGridRow = (line: CacheLine) => ({
  ...line,
  // mapping line.key -> id is necessary for apiRef functions
  // Since `id` is another field of CacheLine, and we want to show it
  // we have to map it to another key
  original_id: line.id,
  id: line.key,
});

const updateRows = (
  apiRef: RefObject<GridApiPremium | null>,
  lines: CacheLine[]
) => {
  if (!apiRef.current) {
    return;
  }
  const existingRowIds = apiRef.current.getAllRowIds();
  const nextIds = new Set(lines.map((l) => l.key));

  const rowsToDelete = existingRowIds.filter(
    (id) => !nextIds.has(id as string)
  );

  apiRef.current.updateRows(lines.map(toGridRow));

  if (rowsToDelete.length) {
    apiRef.current.updateRows(
      rowsToDelete.map((id) => ({ id, _action: "delete" as const }))
    );
  }
};

export const CacheTable = ({
  lines,
  invalidateKeys,
  isLoading,
}: {
  lines: CacheLine[];
  invalidateKeys: (keys: string[]) => Promise<unknown>;
  isLoading: boolean;
}) => {
  const { t } = useTranslation();
  const { format } = useNumberSerialiser();
  const { toLocalDatetime } = useLocalDatetime();
  const apiRef = useGridApiRef();
  const columns = useMemo(
    () =>
      buildCacheColumns({
        format,
        toLocalDatetime,
        invalidateKeys,
        t,
      }),
    [format, toLocalDatetime, invalidateKeys, t]
  );

  const groupingColDef: DataGridPremiumProps["groupingColDef"] = useMemo(
    () => ({
      headerName: t("category"),
      sortable: true,
      flex: 1,
      leafField: "original_id",
      valueFormatter: (value: string | null): string => (value ? t(value) : ""),
    }),
    [t]
  );

  // instead of passing rows directly, we use apiRef
  // This is because when "rows" change the whole tree is re-calculated and opened groups are closed
  // using this makes it so groups are preserved
  useEffect(() => {
    apiRef.current?.setRows(lines.map(toGridRow));
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- mount-only: full setRows on initial render, incremental updateRows handles subsequent changes
  }, []);
  useEffect(() => {
    if (apiRef.current) {
      updateRows(apiRef, lines);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- apiRef is a ref (stable identity), not a reactive dependency
  }, [lines]);

  return (
    <HotkeyCompliantDataGrid
      columns={columns}
      apiRef={apiRef}
      slotProps={slotProps}
      getAggregationPosition={getAggregationPosition}
      aggregationModel={cacheColumnsAggregationModel}
      aggregationFunctions={cacheColumnsAggregationFunctions}
      initialState={initialState}
      headerFilterHeight={50}
      rowGroupingModel={rowGroupingModel}
      groupingColDef={groupingColDef}
      isGroupExpandedByDefault={isGroupExpandedByDefault}
      showCellVerticalBorder={false}
      showColumnVerticalBorder
      loading={isLoading}
    />
  );
};
